import { useFeatureAccess } from "@src/auth/permissions/FeatureAccessContext";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { Disposition } from "@src/backpack-console/pages/SiteDashboard/Documents/types";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recursiveCamelCaseCipher } from "../../postgrestApi";
import FetchClients from "../client";
import { components } from "../client/lib/generated-d3";
import {
  EquipmentDocumentDuplicate,
  EquipmentFile,
  EquipmentFileRow,
  EquipmentFileType,
} from "./types";
import { useInvalidateEquipmentQueries } from "./useInvalidateEquipmentQueries";

const attachments = {
  useQueryAttachmentInfo,
  usePost,
  useDownloadAttachments,
  useDeleteEquipmentDocs,
  useDeleteEquipmentFile,
  getEquipmentAttachmentInfo,
  checkEquipmentDocumentDuplicate,
  downloadAttachments,
  usePatchEquipmentFile,
  useLinkExistingDocument,
  useReplaceEquipmentFile,
};

export default attachments;

// Shown when an upload is rejected because the document already exists on the site.
// The conflict's equipmentIds/equipmentNames remain on the
// error for callers that need them (e.g. the Replace flow).
const UPLOAD_CONFLICT_MESSAGE =
  "This document is already uploaded and linked to other equipment. To use it here, select 'Link Existing Document' instead.";

export class UploadConflictError extends Error {
  constructor(
    public readonly existingFileId: number,
    public readonly filename: string = "",
    public readonly equipmentIds: number[] = [],
    public readonly equipmentNames: string[] = []
  ) {
    super(UPLOAD_CONFLICT_MESSAGE);
    this.name = "UploadConflictError";
  }
}

const ATTACHMENT_QUERY_KEY = "equipment-attachments";
const SITE_QUERY_PREFIX = "site-";
const EQUIPMENT_QUERY_PREFIX = "equipment-";

// For most queries, we need to pass either siteId or equipmentId.
// If both are passed, we will use equipmentId.
const getQueryKey = ({
  siteId,
  equipmentId,
}: {
  siteId?: number;
  equipmentId?: number;
}) => {
  if (typeof equipmentId === "number")
    return [`${EQUIPMENT_QUERY_PREFIX}${equipmentId}`];
  if (typeof siteId === "number") return [`${SITE_QUERY_PREFIX}${siteId}`];
  return [];
};

// endpoint requires {feature: "Equipment", access: "read"}
async function getEquipmentAttachmentInfo(
  {
    equipmentId,
    siteId,
  }: {
    equipmentId?: number;
    siteId?: number;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data } = await FetchClients.d3.GET("/equipment/files", {
    params: { query: { equipment_id: equipmentId, site_id: siteId } },
    headers: checkFeatureAccessHeader,
  });
  return data && (recursiveCamelCaseCipher(data) as EquipmentFileRow[]);
}

// endpoint requires {feature: "Equipment", access: "read"}
// Returns the conflict payload when a document with `filename` already exists on the
// site, or null when it does not (the endpoint replies 200 + conflict on a match and a
// bodyless non-200 otherwise). Keeping the duplicate logic server-side means we don't
// have to mirror it on the client.
async function checkEquipmentDocumentDuplicate(
  { siteId, filename }: { siteId: number; filename: string },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<EquipmentDocumentDuplicate | null> {
  const { data } = await FetchClients.d3.GET("/equipment/files/duplicate", {
    params: { query: { site_id: siteId, filename } },
    headers: checkFeatureAccessHeader,
  });
  return data
    ? (recursiveCamelCaseCipher(data) as EquipmentDocumentDuplicate)
    : null;
}

function useQueryAttachmentInfo<TSelect = EquipmentFileRow[]>({
  equipmentId,
  siteId,
  select,
}: {
  equipmentId?: number;
  siteId?: number;
  select?: (data?: EquipmentFileRow[]) => TSelect;
}) {
  const featureAccess = useFeatureAccess("Equipment");

  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      getEquipmentAttachmentInfo(
        { equipmentId, siteId },
        checkFeatureAccessHeader
      ),
    {
      queryKey: [
        ATTACHMENT_QUERY_KEY,
        ...getQueryKey({ siteId, equipmentId }),
        "attachment-info",
      ],
      select,
      enabled: !!featureAccess,
      staleTime: 1000 * 60,
    }
  );
}

async function postAttachment(
  file: File,
  equipmentId: number,
  fileType: EquipmentFileType,
  signal?: AbortSignal
) {
  const { error, response: httpResponse } = await FetchClients.d3.POST(
    "/equipment/files",
    {
      params: {
        query: {
          equipment_id: equipmentId,
          file_type: fileType,
          filename: file.name,
        },
      },
      parseAs: "text",
      // @ts-expect-error - TS2740 - Type 'File' is missing the following properties from type 'number[]': length, pop, push, concat, and 28 more.
      // 'File' type technically does not match what the openapi spec expects, but the network request still works
      body: file,
      bodySerializer: (body) => body,
      signal,
    }
  );
  if (httpResponse.status === 409) {
    // Server detected a site-wide duplicate; body is DuplicateDocumentConflict.
    // With parseAs:"text" error is a raw JSON string; handle both defensively.
    let conflict = {
      existing_file_id: 0,
      filename: "",
      equipment_ids: [] as number[],
      equipment_names: [] as string[],
    } as components["schemas"]["DuplicateDocumentConflict"];
    try {
      const raw =
        typeof error === "string" ? error : JSON.stringify(error ?? {});
      conflict = { ...conflict, ...(JSON.parse(raw) as typeof conflict) };
    } catch {
      /* fall through with defaults */
    }
    throw new UploadConflictError(
      conflict.existing_file_id,
      conflict.filename,
      conflict.equipment_ids,
      conflict.equipment_names
    );
  }
  if (!httpResponse.ok) {
    throw new Error("Failed to create equipment attachment");
  }
}

function usePost(siteId: number) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();
  return useMutation({
    mutationFn: async ({
      file,
      type,
      equipmentId,
      signal,
    }: {
      file: File;
      type: EquipmentFileType;
      equipmentId: number;
      signal?: AbortSignal;
    }) => {
      await postAttachment(file, equipmentId, type, signal);
    },
    onSettled: async (_, __, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            ATTACHMENT_QUERY_KEY,
            `${EQUIPMENT_QUERY_PREFIX}${variables.equipmentId}`,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [ATTACHMENT_QUERY_KEY, `${SITE_QUERY_PREFIX}${siteId}`],
        }),
        invalidateEquipmentQueries({
          equipmentIds: [variables.equipmentId],
          siteId,
        }),
      ]);
    },
  });
}

type DownloadAttachmentsParams = {
  file_id?: number;
  equipment_id?: number;
  disposition?: Disposition;
  include_preview?: boolean;
  primary?: boolean;
};
// endpoint requires {feature: "Equipment", access: "read"}
async function downloadAttachments(
  {
    disposition,
    equipment_id,
    file_id,
    include_preview,
    primary,
  }: DownloadAttachmentsParams,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.d3
    .GET(`/equipment/files/download`, {
      params: {
        query: {
          file_id,
          disposition,
          include_preview,
          primary,
          equipment_id,
        },
      },
      headers: checkFeatureAccessHeader,
    })
    .then((res) => res.data && recursiveCamelCaseCipher(res.data)) as Promise<
    EquipmentFile[]
  >;
}
function useDownloadAttachments(params: DownloadAttachmentsParams) {
  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) =>
      downloadAttachments(params, checkFeatureAccessHeader),
    {
      queryKey: [
        ATTACHMENT_QUERY_KEY,
        ...getQueryKey({ equipmentId: params.equipment_id }),
        "download",
        params,
      ],
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function deleteEquipmentDocs(
  {
    fileIds,
    equipmentId,
    throwOnError = false,
  }: {
    fileIds?: number[];
    equipmentId?: number;
    throwOnError?: boolean;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.d3
    .DELETE("/equipment/files", {
      params: { query: { file_ids: fileIds, equipment_id: equipmentId } },
      headers: checkFeatureAccessHeader,
    })
    .then(({ response, error }) => {
      if (response.ok) return;
      if (throwOnError)
        throw new Error((error as unknown as Error)?.message || String(error));
    });
}

// Passing equipmentId to the mutation function will delete all docs for a specific equipment
function useDeleteEquipmentDocs(
  equipmentIdForInvalidation?: number,
  { throwOnError = false }: { throwOnError?: boolean } = {}
) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();

  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (
      {
        fileIds,
        equipmentId,
      }: {
        fileIds?: number[];
        equipmentId?: number;
      },
      checkFeatureAccessHeader
    ) => {
      await deleteEquipmentDocs(
        { fileIds, equipmentId, throwOnError },
        checkFeatureAccessHeader
      );
    },
    {
      onSettled: async (_data, _error, variables) => {
        const invalidationEquipmentId =
          variables.equipmentId ?? equipmentIdForInvalidation;
        return queryClient.invalidateQueries({
          queryKey: await Promise.all([
            ATTACHMENT_QUERY_KEY,
            // invalidate equipment query if equipmentId is passed
            // otherwise, if bulk deleting files by id, invalidate site query
            ...getQueryKey({
              equipmentId: invalidationEquipmentId,
              siteId: typeof variables.fileIds !== "undefined" ? 0 : undefined,
            }),
            invalidateEquipmentQueries({
              equipmentIds: invalidationEquipmentId
                ? [invalidationEquipmentId]
                : undefined,
            }),
          ]),
        });
      },
    }
  );
}

async function deleteEquipmentFileById(
  {
    fileId,
    equipmentId,
    throwOnError = false,
  }: {
    fileId: number;
    equipmentId?: number;
    throwOnError?: boolean;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.d3
    .DELETE("/equipment/files/{file_id}", {
      params: {
        path: { file_id: fileId },
        query: { equipment_id: equipmentId },
      },
      headers: checkFeatureAccessHeader,
    })
    .then(({ response, error }) => {
      if (response.ok) return;
      if (throwOnError)
        throw new Error((error as unknown as Error)?.message || String(error));
    });
}

function useDeleteEquipmentFile(
  equipmentIdForInvalidation?: number,
  {
    throwOnError = false,
    siteId,
  }: { throwOnError?: boolean; siteId?: number } = {}
) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();

  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (
      {
        fileId,
        equipmentId,
      }: {
        fileId: number;
        equipmentId?: number;
      },
      checkFeatureAccessHeader
    ) => {
      await deleteEquipmentFileById(
        { fileId, equipmentId, throwOnError },
        checkFeatureAccessHeader
      );
    },
    {
      onSettled: async (_data, _error, variables) => {
        const invalidationEquipmentId =
          variables.equipmentId ?? equipmentIdForInvalidation;
        const invalidations: Promise<unknown>[] = [
          queryClient.invalidateQueries({
            queryKey: [
              ATTACHMENT_QUERY_KEY,
              // siteId: 0 is a sentinel — when no specific equipment is targeted,
              // invalidate the site-level attachment query (key prefix "site-0")
              ...getQueryKey({
                equipmentId: invalidationEquipmentId,
                siteId: invalidationEquipmentId ? undefined : 0,
              }),
            ],
          }),
          invalidateEquipmentQueries({
            equipmentIds: invalidationEquipmentId
              ? [invalidationEquipmentId]
              : undefined,
            siteId,
          }),
        ];
        if (siteId) {
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: [ATTACHMENT_QUERY_KEY, ...getQueryKey({ siteId })],
            })
          );
        }
        return Promise.all(invalidations);
      },
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
function patchEquipmentFile({
  fileId,
  filename,
  notes,
  equipmentIds,
  checkFeatureAccessHeader,
}: {
  fileId: number;
  filename: string;
  notes?: { id?: number; description: string; page: string }[];
  equipmentIds?: number[];
  checkFeatureAccessHeader: CheckFeatureAccessHeader;
}) {
  return FetchClients.d3
    .PATCH("/equipment/files/{file_id}", {
      params: { path: { file_id: fileId } },
      body: {
        filename,
        notes,
        ...(equipmentIds ? { equipment_ids: equipmentIds } : {}),
      },
      headers: checkFeatureAccessHeader,
    })
    .then(({ data, response }) => {
      if (!response.ok) throw new Error("Failed to update equipment file");
      return data;
    });
}

function useLinkExistingDocument(siteId: number) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();

  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (
      { equipmentId, fileIds }: { equipmentId: number; fileIds: number[] },
      checkFeatureAccessHeader
    ) =>
      FetchClients.d3
        .POST("/equipment/files/link", {
          body: { equipment_id: equipmentId, file_ids: fileIds },
          headers: checkFeatureAccessHeader,
        })
        .then(({ response, error }) => {
          if (!response.ok)
            throw new Error(
              (error as unknown as Error)?.message ?? String(error)
            );
        }),
    {
      onSettled: async (_data, _error, { equipmentId }) => {
        return Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              ATTACHMENT_QUERY_KEY,
              `${EQUIPMENT_QUERY_PREFIX}${equipmentId}`,
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [ATTACHMENT_QUERY_KEY, `${SITE_QUERY_PREFIX}${siteId}`],
          }),
          invalidateEquipmentQueries({ equipmentIds: [equipmentId], siteId }),
        ]);
      },
    }
  );
}

function useReplaceEquipmentFile(siteId: number) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();

  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (
      // equipmentId is not sent to the API — kept in the type for onSettled cache invalidation
      {
        file,
        fileType,
        oldFileId,
      }: {
        file: File;
        fileType: EquipmentFileType;
        equipmentId: number;
        oldFileId: number;
      },
      checkFeatureAccessHeader
    ) =>
      FetchClients.d3.POST("/equipment/files/replace", {
        params: {
          query: {
            old_file_id: oldFileId,
            filename: file.name,
            file_type: fileType,
          },
        },
        // @ts-expect-error - File doesn't match the spec's string body type
        body: file,
        // @ts-expect-error - bodySerializer parameter type differs from spec's string body type
        bodySerializer: (body: File) => body,
        parseAs: "text",
        headers: checkFeatureAccessHeader,
      }),
    {
      onSettled: async (_data, _error, { equipmentId }) => {
        return Promise.all([
          queryClient.invalidateQueries({
            queryKey: [
              ATTACHMENT_QUERY_KEY,
              `${EQUIPMENT_QUERY_PREFIX}${equipmentId}`,
            ],
          }),
          queryClient.invalidateQueries({
            queryKey: [ATTACHMENT_QUERY_KEY, `${SITE_QUERY_PREFIX}${siteId}`],
          }),
          invalidateEquipmentQueries({ equipmentIds: [equipmentId], siteId }),
        ]);
      },
    }
  );
}

function usePatchEquipmentFile({
  siteId,
  equipmentId,
}: {
  siteId?: number;
  equipmentId?: number;
}) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (
      {
        fileId,
        filename,
        notes,
        equipmentIds,
      }: {
        fileId: number;
        filename: string;
        notes?: { id?: number; description: string; page: string }[];
        equipmentIds?: number[];
      },
      checkFeatureAccessHeader
    ) =>
      patchEquipmentFile({
        fileId,
        filename,
        notes,
        equipmentIds,
        checkFeatureAccessHeader,
      }),
    {
      onSettled: async () => {
        return queryClient.invalidateQueries({
          queryKey: [
            ATTACHMENT_QUERY_KEY,
            ...getQueryKey({ siteId, equipmentId }),
          ],
        });
      },
    }
  );
}
